// Updated version of AIContentCreatorApp.jsx with brand profile, image generation, and full feature set
import React, { useState, useEffect, useContext } from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, Copy, Download, RefreshCw, Pencil, ImagePlus } from 'lucide-react';
import AppShell from '../../components/AppShell';
import jsPDF from 'jspdf';
import { AuthContext } from '../../context/AuthContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_API_BASE;

const imageSizes = {
  blog: '1024x1024',
  caption: '1024x1024',
  linkedin: '1792x1024',
  facebook: '1792x1024',
  instagram: '1024x1024',
  tiktok: '1024x1792',
  twitter: '1792x1024',
  youtube: '1792x1024',
  productdescription: '1024x1024',
  script: '1792x1024',
  email: '1024x1024',
  ad: '1024x1024',
  banner: '1792x1024',
  brochure: '1024x1792',
  flyer: '1024x1792',
  infographics: '1024x1792',
  thumbnail: '1792x1024',
};


export default function AIContentCreatorApp() {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [output, setOutput] = useState(t('default_output'));
  const [contentType, setContentType] = useState('');
  const [tone, setTone] = useState('');
  const [audience, setAudience] = useState('');
  const [keywords, setKeywords] = useState('');
  const [wordCount, setWordCount] = useState('');
  const [language, setLanguage] = useState('English');
  const [brandProfile, setBrandProfile] = useState('');
  const [brandProfiles, setBrandProfiles] = useState([]);
  const [imageDesc, setImageDesc] = useState('');
  const [ imageUrl, setImageUrl] = useState(null);
  const [imageUrls, setImageUrls] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [score, setScore] = useState(null);
  const [suggestions, setSuggestions] = useState('');
  const [includeImage, setIncludeImage] = useState(true);
  
  useEffect(() => {
    document.title = t('Content Creator') + "– Ennoverse";
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const [templateRes, profileRes] = await Promise.all([
        fetchWithAuth(`${API}/api/templates`),
        fetchWithAuth(`${API}/api/brand-profile/list`)

      ]);

      if (templateRes.ok) setTemplates(await templateRes.json());
      if (profileRes.ok) setBrandProfiles(await profileRes.json());
    };
    fetchData();
  }, []);

  const handleGenerate = async () => {
    if (!imageDesc.trim()) return alert(t('please_provide_image_description'));
    setIsLoading(true);
    try {
      const profile = brandProfiles.find(p => p._id === brandProfile);
      const size = imageSizes[contentType] || '1024x1024';

      const ctaRes = await fetchWithAuth(`${API}/api/content-generator/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contentType,
          tone,
          audience,
          keywords,
          wordCount: 15,
          language,
          prompt: imageDesc
        })
      });

      let ctaText = '';
      if (ctaRes.ok) {
        const ctaData = await ctaRes.json();
        ctaText = ctaData.generatedText?.trim();
        setOutput(ctaText);
      }

      let finalPrompt = imageDesc;

      if (includeImage) {
        const promptRes = await fetchWithAuth(`${API}/api/content-generator/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contentType,
            tone,
            audience,
            keywords,
            wordCount: 100,
            language,
            prompt: `Generate a photorealistic DALL·E prompt based on this image description: "${imageDesc}". Include theme using color: "${profile?.colors?.[0]}", font: "${profile?.font}". Include visual suggestion for CTA: "${ctaText}".`
          })
        });

        if (promptRes.ok) {
          const promptData = await promptRes.json();
          finalPrompt = promptData.generatedText?.trim();
        }

        finalPrompt = `Create a highly detailed, photorealistic image based on the following description: ${imageDesc}.

Style guide:
- Modern commercial photography
- Shot in a high-end studio, soft natural light
- Captured with DSLR camera, 50mm lens, shallow depth of field
- Realistic facial features, clean background, natural skin tones
- Ambient glow and sharp lighting
- Scene should reflect sophistication and professionalism

Additional cues:
- Mood: confident, modern, elegant
- Avoid showing actual text or logos unless stylistically blurred`;

        const imgRes = await fetchWithAuth(`${API}/api/image-generator`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: `${finalPrompt}
Shot with a Canon EOS R5, 85mm f/1.4 lens, ultra-HD cinematic lighting, professional studio photography, realistic human proportions, soft shadows.`,
            brandProfile,
            size,
            logo: profile?.logoPreview || '',
            color: profile?.colors?.[0] || '',
            font: profile?.font || '',
            cta: ctaText,
            n:2,
          })
        });

        const data = await imgRes.json();
const urls = data.imageUrls || [data.imageUrl].filter(Boolean); // fallback if only one image
setImageUrls(urls);

console.log(user);
        if (user?.userId) {
          const contentsaved = await fetchWithAuth(`${API}/api/content`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: user.userId,
              contentType,
              tone,
              audience,
              keywords,
              wordCount,
              language,
              generatedText: ctaText,
              ctaText,
              imagePrompt: finalPrompt,
              imageUrl: urls
            })
          });
          console.log(contentsaved);
        }
      }
    } catch (err) {
      console.error(err);
      alert(t('image_generation_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    const name = prompt(t('template_prompt'));
    if (!name || !user?.userId) return;

    const templateData = {
      contentType,
      tone,
      audience,
      keywords,
      wordCount,
      language,
      brandProfile,
    };

    try {
      const res = await fetchWithAuth(`${API}/api/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, data: templateData }),
      });
      if (res.ok) {
        const newTemplate = await res.json();
        setTemplates(prev => [newTemplate, ...prev]);
        alert(t('template_saved'));
      } else {
        alert(t('template_failed'));
      }
    } catch (err) {
      console.error('Error saving template:', err);
      alert(t('template_failed'));
    }
  };

  return (
    <AppShell>
      <div className="col-span-12 space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-semibold flex items-center justify-center gap-2">
            <Pencil className="w-5 h-5 text-primary" /> {t('ai_content_creator')}
          </h1>
          <p className="text-gray-500 text-sm">{t('page_description')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4 space-y-4">
              {templates.length > 0 && (
                <select onChange={(e) => {
                  const selected = templates.find(t => t._id === e.target.value);
                  if (selected) {
                    const d = selected.data;
                    setContentType(d.contentType || '');
                    setTone(d.tone || '');
                    setAudience(d.audience || '');
                    setKeywords(d.keywords || '');
                    setWordCount(d.wordCount || '');
                    setLanguage(d.language || 'English');
                    setBrandProfile(d.brandProfile || '');
                  }
                }} className="w-full border rounded p-2">
                  <option value="">-- {t('select_template')} --</option>
                  {templates.map(t => (
                    <option key={t._id} value={t._id}>{t.name}</option>
                  ))}
                </select>
              )}
<label className="block text-sm font-medium mb-1">{t('language')}</label>
              <select className="w-full border rounded p-2" value={language} onChange={e => setLanguage(e.target.value)}>
                <option>English</option>
                <option>Hindi</option>
                <option>Spanish</option>
                <option>French</option>
              </select>
              <label className="block text-sm font-medium mb-1">{t('content_type')}</label>
              <select className="w-full border rounded p-2" value={contentType} onChange={e => setContentType(e.target.value)}>
                <option value="">{t('select_content_type')}</option>
                <option value="blog">Blog Post</option>
      <option value="caption">Social Media Caption</option>
      <option value="linkedin">LinkedIn Post</option>
      <option value="facebook">Facebook Post</option>
      <option value="instagram">Instagram Post</option>
      <option value="tiktok">TikTok Video</option>
      <option value="twitter">Twitter Post</option>
      <option value="youtube">YouTube Thumbnail</option>
      <option value="productdescription">Product Description</option>
      <option value="script">Video Script</option>
      <option value="email">Email</option>
      <option value="ad">Ad Copy</option>
      <option value="banner">Website Banner</option>
      <option value="brochure">Brochure</option>
      <option value="flyer">Flyer</option>
      <option value="infographics">Infographic</option>
      <option value="thumbnail">Video Thumbnail</option>
              </select>
              <label className="block text-sm font-medium mb-1">{t('tone')}</label>
              <select className="w-full border rounded p-2" value={tone} onChange={e => setTone(e.target.value)}>
                <option value="">{t('selectTone')}</option>
                <option value="professional">Professional</option>
                <option value="friendly">Friendly</option>
                <option value="witty">Witty</option>
                <option value="persuasive">Persuasive</option>
              </select>
              <label className="block text-sm font-medium mb-1">{t('brand_profile')}</label>
              <select className="w-full border rounded p-2" value={brandProfile} onChange={e => setBrandProfile(e.target.value)}>
                <option value="">-- {t('select_brand_profile')} --</option>
                {brandProfiles.map(profile => (
                  <option key={profile._id} value={profile._id}>{profile.companyName}</option>
                ))}
              </select>
              <label className="block text-sm font-medium mb-1">{t('audience')}</label>
              <Input placeholder={t('audience')} value={audience} onChange={e => setAudience(e.target.value)} />
              <label className="block text-sm font-medium mb-1">{t('keywords')}</label>
              <Input placeholder={t('keywords')} value={keywords} onChange={e => setKeywords(e.target.value)} />
              <label className="block text-sm font-medium mb-1">{t('wordCount')}</label>
              <Input type="number" placeholder={t('wordCount')} value={wordCount} onChange={e => setWordCount(e.target.value)} />
              <label className="block text-sm font-medium mb-1">{t('image_prompt')}</label>
              <Textarea placeholder={t('image_prompt')} value={imageDesc} onChange={e => setImageDesc(e.target.value)} />
              <div className="flex items-center gap-3">
          <label className="text-sm font-medium">{t('include_image_generation')}</label>
          <button
            type="button"
            role="switch"
            aria-checked={includeImage}
            onClick={() => setIncludeImage(!includeImage)}
            className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${includeImage ? 'bg-green-500' : 'bg-gray-300'}`}
          >
            <span
              className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 ${includeImage ? 'translate-x-5' : ''}`}
            />
          </button>
        </div>

        <Button onClick={handleGenerate} disabled={isLoading} className="w-full bg-primary">
          {isLoading && <Loader2 className="animate-spin w-4 h-4 mr-2" />} {t('generate')}
        </Button>

        
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-4">
              <h3 className="text-lg font-semibold">{t('generatedOutput')}</h3>
              <Textarea className="min-h-[250px] w-full" value={output} readOnly />

              {score && (
                <div className="bg-green-50 p-3 rounded text-sm whitespace-pre-wrap">
                  <h4 className="font-semibold mb-1">{t('contentScore')}</h4>
                  <pre className="break-words whitespace-pre-wrap">{score}</pre>
                </div>
              )}

              {suggestions && (
                <div className="bg-yellow-50 p-3 rounded text-sm whitespace-pre-wrap">
                  <h4 className="font-semibold mb-1">{t('suggestions')}</h4>
                  <pre className="break-words whitespace-pre-wrap">{suggestions}</pre>
                </div>
              )}

{imageUrls.length > 0 && (
  <div className="w-full gap-4">
    {imageUrls.map((url, index) => (
      <div key={index} className="border rounded overflow-hidden">
        <img src={url} alt={`Generated ${index + 1}`} className="w-full" />
        <div className="flex justify-end gap-2 p-2">
          <Button size="sm" variant="outline" onClick={() => {
            const link = document.createElement('a');
            link.href = url;
            link.download = `generated-image-${index + 1}.jpg`;
            link.click();
          }}>Download</Button>
        </div>
      </div>
    ))}
  </div>
)}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
